/** App IndexedDB: version, stores, and transaction helpers. */
export const APP_DB_NAME = 'modelmatic-app'

export const APP_DB_VERSION = 2

export const PENDING_RECORDINGS_STORE = 'pending_recordings' as const

export type AppObjectStoreName = typeof PENDING_RECORDINGS_STORE

const INITIAL_STORES: readonly AppObjectStoreName[] = [PENDING_RECORDINGS_STORE]

export function openAppDb(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(APP_DB_NAME, APP_DB_VERSION)

		request.onerror = () => {
			reject(request.error ?? new Error('Failed to open IndexedDB'))
		}

		request.onsuccess = () => {
			resolve(request.result)
		}

		request.onupgradeneeded = (event) => {
			const db = (event.target as IDBOpenDBRequest).result
			const oldVersion = event.oldVersion

			if (oldVersion > 0 && oldVersion < 2 && db.objectStoreNames.contains(PENDING_RECORDINGS_STORE)) {
				db.deleteObjectStore(PENDING_RECORDINGS_STORE)
			}

			for (const name of INITIAL_STORES) {
				if (!db.objectStoreNames.contains(name)) {
					const store = db.createObjectStore(name, { keyPath: 'id' })
					store.createIndex('by_project', 'projectId', { unique: false })
				}
			}
		}
	})
}

export function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		request.onsuccess = () => {
			resolve(request.result)
		}
		request.onerror = () => {
			reject(request.error ?? new Error('IndexedDB request failed'))
		}
	})
}

function idbTransactionDone(tx: IDBTransaction): Promise<void> {
	return new Promise((resolve, reject) => {
		tx.oncomplete = () => {
			resolve()
		}
		tx.onerror = () => {
			reject(tx.error ?? new Error('IndexedDB transaction failed'))
		}
		tx.onabort = () => {
			reject(tx.error ?? new Error('IndexedDB transaction aborted'))
		}
	})
}

export async function withStore<T>(
	mode: IDBTransactionMode,
	storeName: AppObjectStoreName,
	fn: (store: IDBObjectStore) => Promise<T>
): Promise<T> {
	const db = await openAppDb()
	const tx = db.transaction(storeName, mode)
	const store = tx.objectStore(storeName)
	const done = idbTransactionDone(tx)
	const result = await fn(store)
	await done
	return result
}
